/**
 * LinkedIn Connect Panel (Wizard-Schritt „LinkedIn")
 *
 * Zeigt die LinkedIn-Konten des Workspaces mit ihrem Verbindungsstand und öffnet zum
 * Verbinden denselben Dialog wie die Kontoverwaltung (LinkedinConnectDialogHost):
 * feste IP, Gerät, Anmeldung, 2FA, Captcha, Vertragswahl. Vorher hatte der Wizard ein
 * eigenes Formular, das an der normalen Kontoanlage (Lizenz-Sitz, feste IP) vorbeiging.
 */

import React, { useState } from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import LockIcon from '@mui/icons-material/Lock';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'react-i18next';
import { ProductGroups, Products } from 'enums';
import { useLicenseStatus } from 'hooks/use-license-status';
import { useLinkedinAccountsQuery } from 'features/co-pilot/linkedin-accounts/hooks/use-linkedin-accounts-query';
import {
  LinkedinAccountStatuses,
  normalizeAccountStatus,
} from 'features/co-pilot/linkedin-accounts/linkedin-accounts.types';
import LinkedinConnectDialogHost, {
  LinkedinConnectRequest,
} from 'features/co-pilot/linkedin-accounts/linkedin-connect-dialog-host';
import { getAccountProblem } from 'features/ui/app-layout/topbar/linkedin-connection.logic';

interface LinkedInConnectPanelProps {
  /** 'panel' = eigenständiges rechtes Panel; 'workspace' = eingebettet im Arbeitsbereich */
  variant?: 'panel' | 'workspace';
  onClose: () => void;
  /** Nach dem Schließen des Verbinden-Dialogs — der Wizard lädt seinen Stand neu. */
  onSubmitted?: () => void;
  /** Konto verbunden, Erfolgsschritt bestätigt — weiter zum nächsten Schritt. */
  onComplete?: () => void;
}

const LinkedInConnectPanel: React.FC<LinkedInConnectPanelProps> = ({ variant = 'panel', onClose, onSubmitted, onComplete }) => {
  const { classes, cx } = useStyles();
  const { t, i18n } = useTranslation();
  const isDe = i18n.language === 'de';

  const { data: accounts = [], refetch } = useLinkedinAccountsQuery({ refetchInterval: 15 * 1000 });
  const hasSalesLicense = useLicenseStatus(Products.SAIA_SALES) !== 'none';
  const [request, setRequest] = useState<LinkedinConnectRequest | null>(null);

  const rows = accounts.map((account) => {
    const problem = getAccountProblem(account, t);
    const connecting = normalizeAccountStatus(account.status) === LinkedinAccountStatuses.PENDING;
    return {
      account,
      name: [account.firstName, account.lastName].filter(Boolean).join(' ') || account.userEmail,
      problem,
      connecting,
    };
  });
  const hasProblem = rows.some((row) => row.problem);

  const handleDialogClosed = () => {
    setRequest(null);
    void refetch();
    onSubmitted?.();
  };

  return (
    <Box className={cx(classes.panel, variant === 'workspace' && 'workspace')}>
      {variant === 'panel' && (
        <Box className={classes.header}>
          <Box className={classes.headerTitle}>
            <LinkedInIcon className={classes.headerIcon} />
            <Typography variant="subtitle2" className={classes.title}>
              {isDe ? 'Schritt 2: LinkedIn verbinden' : 'Step 2: Connect LinkedIn'}
            </Typography>
          </Box>
          <IconButton size="small" onClick={onClose} aria-label={isDe ? 'Schließen' : 'Close'}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      )}

      {rows.length === 0 || hasProblem ? (
        <Box className={classes.gapBanner}>
          <span className={classes.gapDot}>!</span>
          <Typography variant="caption" className={classes.gapText}>
            {rows.length === 0
              ? isDe
                ? 'Fehlt in diesem Schritt: verbundener LinkedIn-Account'
                : 'Missing in this step: connected LinkedIn account'
              : isDe
                ? 'Ein LinkedIn-Account ist nicht sauber verbunden — bitte erneut anmelden'
                : 'A LinkedIn account is not properly connected — please sign in again'}
          </Typography>
        </Box>
      ) : (
        <Box className={cx(classes.gapBanner, classes.doneBanner)}>
          <span className={cx(classes.gapDot, classes.doneDot)}>✓</span>
          <Typography variant="caption" className={cx(classes.gapText, classes.doneText)}>
            {isDe ? 'LinkedIn verbunden' : 'LinkedIn connected'}
          </Typography>
        </Box>
      )}

      <Box className={classes.body}>
        {rows.length > 0 && (
          <Box className={classes.card}>
            <Box className={classes.cardHeader}>
              <Typography variant="caption" className={classes.sectionLabel}>
                {isDe ? 'Deine LinkedIn-Accounts' : 'Your LinkedIn accounts'}
              </Typography>
              <button type="button" className={classes.linkButton} onClick={() => setRequest({ mode: 'new' })}>
                {isDe ? '+ Weiteren Account verbinden' : '+ Connect another account'}
              </button>
            </Box>
            {rows.map((row) => (
              <Box key={row.account._id} className={classes.accountRow}>
                <Box className={classes.accountText}>
                  <Typography variant="body2" className={classes.accountName}>
                    {row.name}
                  </Typography>
                  <Typography
                    variant="caption"
                    className={cx(classes.accountStatus, row.problem ? 'problem' : row.connecting ? 'connecting' : 'ok')}
                  >
                    {row.problem
                      ? row.problem.label
                      : row.connecting
                        ? isDe ? 'Wird verbunden…' : 'Connecting…'
                        : isDe ? 'Verbunden' : 'Connected'}
                  </Typography>
                  {row.problem?.detail ? (
                    <Typography variant="caption" className={classes.accountDetail}>
                      {row.problem.detail}
                    </Typography>
                  ) : null}
                </Box>
                {row.problem ? (
                  <button
                    type="button"
                    className={classes.secondaryButton}
                    onClick={() => setRequest({ mode: 'resync', account: row.account })}
                  >
                    {isDe ? 'Erneut anmelden' : 'Sign in again'}
                  </button>
                ) : row.connecting ? null : (
                  <CheckCircleIcon className={classes.successIcon} />
                )}
              </Box>
            ))}
          </Box>
        )}

        <Box className={classes.securityNote}>
          <LockIcon className={classes.lockIcon} />
          <Typography variant="caption" className={classes.securityText}>
            {isDe
              ? 'Im Verbinden-Fenster sichern wir eine feste IP, bereiten ein Gerät mit der LinkedIn-App vor und melden dich an. Codes, Captcha oder die Vertragswahl fragen wir direkt dort ab.'
              : 'In the connect window we secure a fixed IP, prepare a device with the LinkedIn app and sign you in. Codes, captcha or the contract choice are asked right there.'}
          </Typography>
        </Box>

        <Box className={classes.footer}>
          <Typography variant="caption" className={classes.footerHint}>
            {rows.length > 0
              ? isDe ? 'Weitere Accounts kannst du oben jederzeit verbinden.' : 'You can connect more accounts above any time.'
              : isDe ? 'Dauert meist ein paar Minuten.' : 'Usually takes a few minutes.'}
          </Typography>
          {/* Ein Knopf: ohne Account verbinden, sonst weiter. */}
          {rows.length > 0 && onComplete ? (
            <button type="button" className={classes.saveButton} onClick={onComplete}>
              {isDe ? 'Weiter' : 'Next'}
            </button>
          ) : (
            <button type="button" className={classes.saveButton} onClick={() => setRequest({ mode: 'new' })}>
              {isDe ? 'LinkedIn-Account verbinden' : 'Connect LinkedIn account'}
            </button>
          )}
        </Box>
      </Box>

      <LinkedinConnectDialogHost
        request={request}
        productGroup={hasSalesLicense ? ProductGroups.LEAD_BOOSTER : ProductGroups.HR_BOOSTER}
        onClose={handleDialogClosed}
        onConnected={onComplete}
      />
    </Box>
  );
};

const useStyles = makeStyles()((theme) => ({
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
  },
  linkButton: {
    border: 'none',
    background: 'none',
    padding: 0,
    color: theme.palette.primary.main,
    fontFamily: 'inherit',
    fontSize: 12.5,
    fontWeight: 700,
    cursor: 'pointer',
  },
  accountRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    padding: '8px 0',
    '& + &': {
      borderTop: `1px solid ${theme.palette.divider}`,
    },
  },
  accountText: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
  },
  accountName: {
    fontWeight: 600,
  },
  accountStatus: {
    fontWeight: 600,
    '&.ok': { color: theme.palette.success.main },
    '&.connecting': { color: theme.palette.info.main },
    '&.problem': { color: theme.palette.warning.dark },
  },
  accountDetail: {
    color: theme.palette.text.secondary,
  },
  secondaryButton: {
    flexShrink: 0,
    border: `1px solid ${theme.palette.primary.main}`,
    background: '#fff',
    color: theme.palette.primary.main,
    borderRadius: 10,
    padding: '6px 12px',
    fontFamily: 'inherit',
    fontSize: 12.5,
    fontWeight: 700,
    cursor: 'pointer',
  },
  panel: {
    width: 380,
    flexShrink: 0,
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    borderLeft: `1px solid ${theme.palette.greys.lighter}`,
    backgroundColor: theme.palette.backgrounds.main_new,
    boxSizing: 'border-box',
    '&.workspace': {
      width: '100%',
      maxWidth: 620,
      height: 'auto',
      borderLeft: 'none',
      backgroundColor: 'transparent',
    },
  },
  header: {
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    padding: '13px 16px',
    borderBottom: `1px solid ${theme.palette.greys.lighter}`,
    backgroundColor: theme.palette.background.paper,
  },
  headerTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    minWidth: 0,
  },
  headerIcon: {
    color: '#0A66C2',
    fontSize: '1.35rem',
  },
  title: {
    fontWeight: 700,
    color: theme.palette.text.primary,
    whiteSpace: 'nowrap',
  },
  gapBanner: {
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '9px 16px',
    backgroundColor: '#FFF8EC',
    borderBottom: '1px solid #F6E4C4',
  },
  gapDot: {
    flexShrink: 0,
    width: 16,
    height: 16,
    borderRadius: '50%',
    backgroundColor: '#FFB547',
    color: '#fff',
    fontSize: 10,
    fontWeight: 700,
    display: 'grid',
    placeItems: 'center',
  },
  gapText: {
    color: '#8A6320',
    lineHeight: 1.4,
  },
  doneBanner: {
    backgroundColor: '#EAFBF4',
    borderBottom: '1px solid #C6EEDD',
  },
  doneDot: {
    backgroundColor: '#05CD99',
  },
  doneText: {
    color: '#0B7050',
  },
  body: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    padding: 16,
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
  },
  card: {
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.greys.lighter}`,
    borderRadius: 16,
    padding: 16,
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
  },
  sectionLabel: {
    fontSize: 11,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    fontWeight: 700,
    color: theme.palette.text.secondary,
  },
  row: {
    display: 'grid',
    gridTemplateColumns: '1.4fr 1fr',
    gap: 12,
  },
  securityNote: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  lockIcon: {
    fontSize: '1rem',
    color: theme.palette.text.secondary,
  },
  securityText: {
    color: theme.palette.text.secondary,
    lineHeight: 1.4,
  },
  error: {
    color: theme.palette.error.main,
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
  },
  footerHint: {
    color: theme.palette.text.secondary,
    lineHeight: 1.4,
  },
  saveButton: {
    flexShrink: 0,
    border: 'none',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: 13,
    fontWeight: 700,
    color: theme.palette.primary.contrastText,
    backgroundColor: theme.palette.primary.main,
    borderRadius: 10,
    padding: '9px 18px',
    '&:hover': {
      opacity: 0.9,
    },
    '&:disabled': {
      opacity: 0.6,
      cursor: 'default',
    },
  },
  successRow: {
    display: 'flex',
    gap: 10,
    alignItems: 'flex-start',
  },
  successIcon: {
    color: '#05CD99',
    fontSize: '1.3rem',
    flexShrink: 0,
  },
}));

export default LinkedInConnectPanel;
